import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  deleteDoc,
  updateDoc,
  query,
  orderBy,
  serverTimestamp,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from './firebase';
import { Chat, Message } from '../types';

// ─── Firestore paths ───
// users/{uid}/chats/{chatId}

const getUserChatsRef = (uid: string) => collection(db, 'users', uid, 'chats');
const getChatDocRef = (uid: string, chatId: string) => doc(db, 'users', uid, 'chats', chatId);

// ─── Firestore document shape ───
interface FirestoreChat {
  title: string;
  messages: FirestoreMessage[];
  sessionId: string | null;
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
}

interface FirestoreMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: number;
  sources?: any[];
  attachments?: any[];
  mode?: string;
  lang?: string;
  error?: string;
}

// ─── Convert App → Firestore ───
const chatToFirestore = (chat: Chat): FirestoreChat => {
  const messages: FirestoreMessage[] = chat.messages.map((msg) => {
    const fMsg: FirestoreMessage = {
      id: msg.id,
      role: msg.role,
      content: msg.content,
      timestamp: msg.timestamp,
    };
    if (msg.sources && msg.sources.length > 0) fMsg.sources = msg.sources;
    if (msg.attachments && msg.attachments.length > 0) {
      // Don't save blob URLs — strip previewUrl
      fMsg.attachments = msg.attachments.map((a) => ({
        name: a.name,
        type: a.type,
        size: a.size,
      }));
    }
    if (msg.mode) fMsg.mode = msg.mode;
    if (msg.lang) fMsg.lang = msg.lang;
    if (msg.error) fMsg.error = msg.error;
    return fMsg;
  });

  return {
    title: chat.title,
    messages,
    sessionId: chat.sessionId || null,
    createdAt: chat.createdAt ? Timestamp.fromMillis(chat.createdAt) : null,
    updatedAt: chat.updatedAt ? Timestamp.fromMillis(chat.updatedAt) : null,
  };
};

// ─── Convert Firestore → App ───
const firestoreToChat = (chatId: string, data: FirestoreChat, uid: string): Chat => {
  return {
    id: chatId,
    title: data.title || 'New chat',
    messages: (data.messages || []).map((msg) => ({
      id: msg.id,
      role: msg.role,
      content: msg.content,
      timestamp: msg.timestamp,
      sources: msg.sources,
      attachments: msg.attachments,
      mode: msg.mode as any,
      lang: msg.lang as any,
      error: msg.error,
      isStreaming: false,
    })),
    sessionId: data.sessionId || null,
    createdAt: data.createdAt?.toMillis() || Date.now(),
    updatedAt: data.updatedAt?.toMillis() || Date.now(),
    userId: uid,
  };
};

// ─── CRUD Operations ───

/**
 * Save or update a single chat in Firestore
 */
export const saveChat = async (uid: string, chat: Chat): Promise<void> => {
  const docRef = getChatDocRef(uid, chat.id);
  const data = chatToFirestore(chat);
  await setDoc(docRef, data, { merge: true });
};

/**
 * Load a single chat from Firestore
 */
export const loadChat = async (uid: string, chatId: string): Promise<Chat | null> => {
  const docRef = getChatDocRef(uid, chatId);
  const snap = await getDoc(docRef);
  if (!snap.exists()) return null;
  return firestoreToChat(chatId, snap.data() as FirestoreChat, uid);
};

/**
 * Load all chats for a user, ordered by updatedAt desc
 */
export const loadAllChats = async (uid: string): Promise<Chat[]> => {
  const q = query(getUserChatsRef(uid), orderBy('updatedAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => firestoreToChat(d.id, d.data() as FirestoreChat, uid));
};

/**
 * Delete a single chat
 */
export const deleteChatFromFirestore = async (uid: string, chatId: string): Promise<void> => {
  await deleteDoc(getChatDocRef(uid, chatId));
};

/**
 * Update only the title of a chat
 */
export const updateChatTitle = async (uid: string, chatId: string, title: string): Promise<void> => {
  await updateDoc(getChatDocRef(uid, chatId), { title });
};

/**
 * Delete ALL chats for a user
 */
export const deleteAllChats = async (uid: string): Promise<void> => {
  const snap = await getDocs(getUserChatsRef(uid));
  const batch = writeBatch(db);
  snap.docs.forEach((d) => batch.delete(d.ref));
  await batch.commit();
};

/**
 * Migrate chats from localStorage to Firestore (one-time migration)
 */
export const migrateFromLocalStorage = async (uid: string): Promise<number> => {
  const stored = localStorage.getItem(`chats_${uid}`);
  if (!stored) return 0;

  try {
    const chats = JSON.parse(stored) as Chat[];
    if (!chats || chats.length === 0) return 0;

    // Check if user already has chats in Firestore
    const existing = await getDocs(getUserChatsRef(uid));
    if (!existing.empty) {
      // Already has data in Firestore, skip migration
      localStorage.removeItem(`chats_${uid}`);
      return 0;
    }

    // Save all chats to Firestore
    const batch = writeBatch(db);
    chats.forEach((chat) => {
      const ref = getChatDocRef(uid, chat.id);
      const data = chatToFirestore({
        ...chat,
        sessionId: chat.sessionId ?? null,
      });
      batch.set(ref, data);
    });
    await batch.commit();

    // Clean up localStorage after successful migration
    localStorage.removeItem(`chats_${uid}`);

    return chats.length;
  } catch (error) {
    console.error('Migration failed:', error);
    return 0;
  }
};